import { svgIcons } from '../UI/SvgIcons';
import { X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux/store';
import { useDispatch } from 'react-redux';
import { setSideBarOpen } from '@/redux/features/UserSlice';
import { RoutesLink } from '@/utility/RouteLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/context/AuthContext';

const {
  DashboardIcon,
  ProductsIcon,
  OrdersIcon,
  CustomerIcon,
  CategoryIcon,
  BrandsIcon,
  StaffIcon,
  DiscountIcon,
  WebsiteContentIcon,
  ReportIcon,
  SettingIcon,
  DownArrow,
  ReviewIcon,
} = svgIcons;

const sidebarMenu = [
  {
    label: 'Dashboard',
    icon: DashboardIcon,
    relatedRoutes: [RoutesLink.dashboard],
    path: RoutesLink.dashboard,
    resource: [''],
  },
  {
    label: 'Products',
    icon: ProductsIcon,
    path: RoutesLink.products,
    resources: ['product', 'collection'],
    relatedRoutes: [
      RoutesLink.products,
      RoutesLink.addProducts,
      RoutesLink.productDetail,
      RoutesLink.collection,
      RoutesLink?.createCollection,
      `${RoutesLink.collectionDetails}/:id`,
    ],
    children: [
      {
        label: 'Products',
        path: RoutesLink.products,
        relatedRoutes: [
          RoutesLink.products,
          `${RoutesLink.addProducts}/:id`,
          `${RoutesLink.productDetail}/:id`,
        ],
        resource: 'product',
      },
      {
        label: 'Product Manager',
        path: RoutesLink.productManager,
        relatedRoutes: [RoutesLink.productManager],
        resource: 'product',
      },
      {
        label: 'Collection',
        path: RoutesLink.collection,
        relatedRoutes: [
          RoutesLink.collection,
          RoutesLink?.createCollection,
          `${RoutesLink.collectionDetails}/:id`,
        ],
        resource: 'collection',
      },
      {
        label: 'Variants',
        path: RoutesLink.variants,
        relatedRoutes: [RoutesLink?.variants],
        resource: 'collection',
      },
    ],
  },
  {
    label: 'Orders',
    icon: OrdersIcon,
    path: RoutesLink.order,
    relatedRoutes: [
      RoutesLink.order,
      `${RoutesLink.orderDetail}/:id`,
      RoutesLink.refundReturn,
      RoutesLink.abandonedCheckouts,
    ],
    resources: ['order', 'refundReturn', 'abandonedCheckouts'],
    children: [
      {
        label: 'Order List',
        path: RoutesLink.order,
        relatedRoutes: [RoutesLink.order, `${RoutesLink.orderDetail}/:id`],
        resource: 'order',
      },
      {
        label: 'Refund & Return',
        path: RoutesLink.refundReturn,
        relatedRoutes: [RoutesLink.refundReturn],
        resource: 'refundReturn',
      },
      {
        label: 'Abandoned Checkouts',
        path: RoutesLink.abandonedCheckouts,
        relatedRoutes: [RoutesLink.abandonedCheckouts],
        resource: 'abandonedCheckouts',
      },
    ],
  },
  {
    label: 'Users',
    icon: CustomerIcon,
    path: RoutesLink.customer,
    relatedRoutes: [RoutesLink.customer, `${RoutesLink.customerDetails}/:id`],
    resource: 'user',
    children: [
      {
        label: 'Customers',
        icon: CustomerIcon,
        path: RoutesLink.customer,
        relatedRoutes: [
          RoutesLink.customer,
          `${RoutesLink.customerDetails}/:id`,
        ],
        resource: 'user',
      },
      {
        label: 'Roles',
        icon: CustomerIcon,
        path: RoutesLink.roles,
        relatedRoutes: [RoutesLink.addRoles, RoutesLink.roles],
        resource: 'user',
      },
      {
        label: 'Staff',
        icon: StaffIcon,
        path: RoutesLink.staffs,
        relatedRoutes: [RoutesLink.staffs],
        resource: 'user',
      },
    ],
  },
  {
    label: 'Category',
    icon: CategoryIcon,
    path: RoutesLink.categoryList,
    relatedRoutes: [
      RoutesLink.categoryList,
      RoutesLink.addCategory,
      RoutesLink.categoryManager,
    ],
    resource: 'category',
    children: [
      {
        label: 'Category',
        path: RoutesLink.categoryList,
        relatedRoutes: [`${RoutesLink.categoryList}`],
        resource: 'category',
      },
      {
        label: 'Category Manger',
        icon: CustomerIcon,
        path: RoutesLink.categoryManager,
        relatedRoutes: [RoutesLink.categoryManager],
        resource: 'category',
      },
    ],
  },
  {
    label: 'Rating & Review',
    icon: ReviewIcon,
    path: RoutesLink.reviewRatingDetails,
    relatedRoutes: [
      RoutesLink.reviewRatingDetails,
      `${RoutesLink.reviewRatingDetails}/:id`,
    ],
    resource: 'review',
  },
  {
    label: 'Brands',
    icon: BrandsIcon,
    path: RoutesLink.brandsList,
    relatedRoutes: [RoutesLink.brandsList, RoutesLink.addBrands],
    resource: 'brand',
    children: [
      {
        label: 'Brands',
        path: RoutesLink.brandsList,
        relatedRoutes: [`${RoutesLink.brandsList}`],
        resource: 'brand',
      },
      {
        label: 'Brands Manger',
        icon: CustomerIcon,
        path: RoutesLink.brandManager,
        relatedRoutes: [RoutesLink.brandManager],
        resource: 'brand',
      },
    ],
  },
  {
    label: 'Discount',
    icon: DiscountIcon,
    path: '/discount',
    resource: ['discount', 'coupon'],
    relatedRoutes: [
      RoutesLink.addDiscount,
      RoutesLink.discounts,
      RoutesLink.addCoupon,
      RoutesLink.coupons,
    ],
    children: [
      {
        label: 'Discounts',
        path: RoutesLink.discounts,
        relatedRoutes: [RoutesLink.discounts],
        resource: 'discount',
      },
      {
        label: 'Coupons',
        path: RoutesLink.coupons,
        relatedRoutes: [RoutesLink.coupons],
        resource: 'coupons',
      },
    ],
  },
  // {
  //   label: 'Daily Spin',
  //   path: RoutesLink.spinWheels,
  //   relatedRoutes: [RoutesLink.addSpinWheel, RoutesLink.spinWheels],
  //   icon: DailySpinIcon,
  //   resource: 'daily spin',
  // },
  {
    label: 'Website content',
    icon: WebsiteContentIcon,
    path: RoutesLink.websiteContent,
    relatedRoutes: [RoutesLink.websiteContent],
    resource: 'website content',
  },
  {
    label: 'Reports',
    icon: ReportIcon,
    path: RoutesLink.reports,
    relatedRoutes: [RoutesLink.reports],
    resource: 'reports',
  },
  {
    label: 'Settings',
    icon: SettingIcon,
    path: '/settings',
    relatedRoutes: [
      RoutesLink.createEmpDiscount,
      RoutesLink.refundReason,
      RoutesLink.appLayout,
      RoutesLink.checkOutManagement,
    ],
    resource: 'settings',
    children: [
      {
        label: 'General',
        path: RoutesLink.createEmpDiscount,
        relatedRoutes: [RoutesLink.createEmpDiscount],
        resource: 'settings',
      },
      {
        label: 'Refund Reason',
        path: RoutesLink.refundReason,
        relatedRoutes: [RoutesLink.refundReason],
        resource: 'refundReason',
      },
      {
        label: 'App Layout',
        path: RoutesLink.appLayout,
        relatedRoutes: [RoutesLink.appLayout],
        resource: 'refundReason',
      },
      {
        label: 'Checkout Management',
        path: RoutesLink.checkOutManagement,
        relatedRoutes: [RoutesLink.checkOutManagement],
        resource: 'orderView',
      },
    ],
  },
];
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, userDetail } = useAuth();
  const { canView } = usePermissions();
  const location = useLocation();
  const openDropdown = useSelector(
    (state: RootState) => state.user.isSideBarOpen,
  );

  const toggleMenu = (index: number) => {
    dispatch(setSideBarOpen(openDropdown === index ? -1 : index));
  };

  const isRouteActive = (relatedRoutes: string[] = []) => {
    return relatedRoutes?.some((route) => {
      if (route?.includes('/:')) {
        const baseRoute = route?.split('/:')[0];
        return location?.pathname?.startsWith(baseRoute);
      }
      return location?.pathname === route;
    });
  };

  if (isLoading || !userDetail) return null;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-40 lg:hidden z-10"
          onClick={onClose}
        ></div>
      )}
      <aside
        className={`group max-h-[calc(100dvh-83px)] overflow-auto fixed top-1/2 -translate-y-1/2 left-7 bg-white shadow-md p-5 sidebar z-30 transition-transform duration-500 ease-in-out
        lg:translate-x-0 
        ${isOpen ? 'translate-x-0' : '-translate-x-[130%]'} 
        lg:block`}
        style={{ scrollbarWidth: 'none' }}
      >
        <div
          className="flex items-center justify-end lg:hidden absolute right-1 top-1 cursor-pointer"
          onClick={onClose}
        >
          <X />
        </div>
        <ul className="flex-1 flex flex-col items-start justify-between gap-1">
          {!isLoading &&
            sidebarMenu
              .filter(
                (item) =>
                  item.label === 'Dashboard' ||
                  item.resources?.some((r) => canView(r)) ||
                  (Array.isArray(item.resource) &&
                    item.resource.some((r) => canView(r))) ||
                  (typeof item.resource === 'string' && canView(item.resource)),
              )
              .map((item, index) => {
                const isActive = isRouteActive(item.relatedRoutes);
                return (
                  <li key={item.label} className="w-full">
                    {item.children ? (
                      <button
                        onClick={() => item.children && toggleMenu(index)}
                        className={`flex items-center rounded-full hover:bg-primary-light w-full ${openDropdown === index ? 'bg-primary-light text-black' : ''}`}
                      >
                        <span className="h-10 w-10 min-h-10 min-w-10 lg:h-[52px] lg:w-[52px] lg:min-h-[52px] lg:min-w-[52px] flex items-center justify-center rounded-full">
                          {item.icon}
                        </span>
                        <div
                          style={{ scrollbarWidth: 'none' }}
                          className="transition-all flex-1 text-left duration-500 flex items-center justify-between ease-in-out overflow-hidden lg:w-0 lg:opacity-0 group-hover:opacity-100 mr-4 lg:mr-0 group-hover:mr-4 lg:group-hover:w-[128px] text-nowrap group-hover:overflow-auto"
                        >
                          {item.label}
                          {item.children && DownArrow}
                        </div>
                      </button>
                    ) : (
                      <Link
                        style={{ scrollbarWidth: 'none' }}
                        to={item.path}
                        className={`flex items-center rounded-full hover:bg-primary-light w-full ${isActive ? 'bg-primary-light text-black' : 'hover:text-black'}`}
                        onClick={() => toggleMenu(-1)}
                      >
                        <span className="h-10 w-10 min-h-10 min-w-10 lg:h-[52px] lg:w-[52px] lg:min-h-[52px] lg:min-w-[52px] flex items-center justify-center rounded-full">
                          {item.icon}
                        </span>
                        <div
                          style={{ scrollbarWidth: 'none' }}
                          className="transition-all flex-1 text-left duration-500 flex items-center justify-between ease-in-out overflow-hidden lg:w-0 lg:opacity-0 group-hover:opacity-100 mr-4 lg:mr-0 group-hover:mr-4 lg:group-hover:w-[128px] text-nowrap group-hover:overflow-auto"
                        >
                          {item.label}
                          {item.children && DownArrow}
                        </div>
                      </Link>
                    )}

                    {/* Agar children hain to collapse */}
                    {item.children && (
                      <div
                        className={`overflow-hidden hidden group-hover:flex flex-col gap-1 transition-all duration-300 ease-in-out w-0 ${
                          openDropdown === index
                            ? 'max-h-40 w-auto p-2'
                            : 'max-h-0'
                        }`}
                      >
                        {item.children
                          .filter((child) => canView(child.resource))
                          .map((child) => {
                            const isActive = isRouteActive(child.relatedRoutes);
                            return (
                              <Link
                                to={child.path}
                                key={child.label}
                                className={`rounded-full text-sm px-4 py-2.5 hover:bg-primary-light w-full  ${isActive ? 'bg-primary-light text-black' : 'text-gray-300 hover:text-black'}`}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                      </div>
                    )}
                  </li>
                );
              })}
        </ul>
      </aside>
    </>
  );
};

export default Sidebar;
